/**
 * Pinia store for calendar event data management
 *
 * Handles scheduling of vehicle deliveries and returns.
 */

import { defineStore } from 'pinia'
import {
  type CalendarEvent,
  type CalendarEventForm,
  type FullCalendarEvent,
  mapToFullCalendarEvent,
  mapFormToApiParams,
  mapEventToForm
} from '~/api/types/calendarEvent'

export const useCalendarEventStore = defineStore({
  id: 'calendarEvents',

  state: () => ({
    events: {} as Record<string, CalendarEvent>,
    loading: false,
    initialized: false
  }),

  getters: {
    /**
     * Convert all events to FullCalendar format for display
     */
    fullCalendarEvents: (state): FullCalendarEvent[] =>
      Object.values(state.events).map(mapToFullCalendarEvent)
  },

  actions: {
    // ==================== Fetch Operations ====================

    async fetchEvents(api: API) {
      if (!api?.calendarEvents) {
        console.warn('[calendarEventStore] api.calendarEvents not available')
        return
      }

      this.loading = true

      try {
        const response = await api.calendarEvents.getAllCalendarEvents()
        const items = (response.value || []) as CalendarEvent[]

        const newEvents: Record<string, CalendarEvent> = {}
        for (const event of items) {
          newEvents[event.id] = {
            ...event,
            customData: event.customData || {}
          }
        }

        this.events = newEvents
        this.initialized = true
      } catch (e) {
        console.error('[calendarEventStore] Failed to fetch events:', e)
      } finally {
        this.loading = false
      }
    },

    async get(api: API, id: string): Promise<CalendarEvent | null> {
      if (!api?.calendarEvents) {
        console.warn('[calendarEventStore] api.calendarEvents not available')
        return null
      }

      try {
        const response = await api.calendarEvents.getCalendarEvent(id)
        const event = response as CalendarEvent

        this.events[event.id] = {
          ...event,
          customData: event.customData || {}
        }

        return this.events[event.id]
      } catch (e) {
        console.error('[calendarEventStore] Failed to get event:', e)
        return null
      }
    },

    // ==================== CRUD Operations ====================

    async create(api: API, form: CalendarEventForm): Promise<CalendarEvent | null> {
      if (!api?.calendarEvents) {
        throw new Error('CalendarEvents API not available')
      }

      this.loading = true

      try {
        const params = mapFormToApiParams(form)

        const response = await api.calendarEvents.createCalendarEvent(
          params.rentalCar,
          params.damagedCar,
          params.customer,
          params.eventDate,
          params.location,
          params.calendarEvent,
          params.insurance,
          params.customData as Record<string, unknown>
        )

        const newEvent = response as unknown as CalendarEvent

        // Fetch fresh data from BE to ensure state consistency
        return await this.get(api, newEvent.id)
      } catch (e) {
        console.error('[calendarEventStore] Failed to create event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    async update(api: API, id: string, form: CalendarEventForm): Promise<CalendarEvent | null> {
      if (!api?.calendarEvents) {
        throw new Error('CalendarEvents API not available')
      }

      this.loading = true

      try {
        const params = mapFormToApiParams(form)

        await api.calendarEvents.updateCalendarEvent(
          id,
          params.rentalCar,
          params.damagedCar,
          params.customer,
          params.eventDate,
          params.location,
          params.calendarEvent,
          params.insurance,
          params.customData as Record<string, unknown>
        )

        // Fetch fresh data from BE to ensure state consistency
        return await this.get(api, id)
      } catch (e) {
        console.error('[calendarEventStore] Failed to update event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    /**
     * Update event time only (for drag/drop operations)
     */
    async updateEventTime(
      api: API,
      id: string,
      start: Date,
      end: Date
    ): Promise<CalendarEvent | null> {
      if (!api?.calendarEvents) {
        throw new Error('CalendarEvents API not available')
      }

      const existing = this.events[id]
      if (!existing) {
        console.error('[calendarEventStore] Event not found:', id)
        return null
      }

      try {
        // Preserve existing customData but update endDate
        const customData = {
          ...existing.customData,
          endDate: end.getTime()
        }

        await api.calendarEvents.updateCalendarEvent(
          id,
          existing.rentalCar,
          existing.damagedCar,
          existing.customer,
          start.getTime(),
          existing.location,
          existing.calendarEvent,
          existing.insurance,
          customData
        )

        // Fetch fresh data from BE to ensure state consistency
        return await this.get(api, id)
      } catch (e) {
        console.error('[calendarEventStore] Failed to update event time:', e)
        throw e
      }
    },

    async delete(api: API, id: string): Promise<boolean> {
      if (!api?.calendarEvents) {
        throw new Error('CalendarEvents API not available')
      }

      this.loading = true

      try {
        await api.calendarEvents.deleteCalendarEvent(id)
        delete this.events[id]
        return true
      } catch (e) {
        console.error('[calendarEventStore] Failed to delete event:', e)
        throw e
      } finally {
        this.loading = false
      }
    },

    // ==================== Helpers ====================

    /**
     * Get form data for editing an event
     */
    getEventForm(id: string): CalendarEventForm | null {
      const event = this.events[id]
      if (!event) return null
      return mapEventToForm(event)
    }
  }
})

// Re-export types for convenience
export type {
  CalendarEvent,
  CalendarEventForm,
  CalendarEventStatus,
  CalendarEventType,
  FullCalendarEvent
} from '~/api/types/calendarEvent'

export {
  EVENT_TYPE_CONFIG,
  createEmptyCalendarEventForm
} from '~/api/types/calendarEvent'
